import { Text, View } from '@react-pdf/renderer';

import type { CourseProgram, Lesson, NanodegreeProgram, PartProgram, Program } from '~/features/program/models/program';

import { createPdfStyles } from '../utils/create-pdf-styles';

import { styles as sharedStyles } from './syllabus-styles';

type SyllabusCoursesProps = {
  program: Program;
};

const styles = createPdfStyles({
  courseBox: {
    display: 'flex',
    flexDirection: 'column',
    marginVertical: '16px',
  },
  headerBox: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'blue.700',
    color: 'white',
    padding: '16px',
    fontWeight: 600,
  },
  courseIndex: {
    fontSize: '18px',
    color: 'accent.lime',
    marginRight: 8,
    fontFamily: 'PolySans',
    fontWeight: 400,
  },
  courseTitle: {
    fontSize: '18px',
    flex: 1,
    fontFamily: 'PolySans',
    fontWeight: 400,
  },
  courseDuration: {
    backgroundColor: 'accent.lime',
    borderRadius: 12,
    paddingVertical: '6px',
    paddingHorizontal: '3px',
    marginLeft: 8,
    minWidth: '72px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  courseDurationText: {
    color: 'blue.800',
    fontSize: '11px',
    textAlign: 'center',
    fontFamily: 'Open Sans',
    fontWeight: 600,
  },
  lessonsBox: {
    backgroundColor: 'accent.sky.100',
    padding: 12,
    overflow: 'hidden',
  },
  courseSummary: {
    ...sharedStyles.text,
    marginBottom: 12,
  },
  lessonIndexBackgroundPill: {
    backgroundColor: 'blue.700',
    borderRadius: 6,
    paddingVertical: '2px',
    paddingLeft: '2px',
    paddingRight: '4px',
    marginRight: 8,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'flex-start',
    textAlign: 'center',
    position: 'relative',
    top: 2,
  },
  lessonIndex: {
    fontSize: '8px',
    color: '#ffffff',
    textAlign: 'center',
  },
  lessonTitle: {
    ...sharedStyles.text,
    fontSize: '12px',
    flex: 2,
  },
  lessonSummary: {
    ...sharedStyles.text,
    fontSize: '12px',
    flex: 4,
  },
  lessonDisplayRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: 2,
    borderColor: '#E3E7FB',
    borderTopWidth: 1,
    padding: '8px',
  },
});

export function SyllabusCourses({ program }: SyllabusCoursesProps) {
  // Depending on whether the program is a Nanodegree or a Course, we display the parts & the parts' lessons or the lessons directly.
  const parts = (program as NanodegreeProgram).parts || [];
  const lessons = (program as CourseProgram).lessons || [];

  if (parts.length === 0 && lessons.length === 0) {
    return null;
  }

  return (
    <View style={sharedStyles.section}>
      <Text style={sharedStyles.sectionTitle}>Courses</Text>
      {parts.length > 0 &&
        parts.map((part: PartProgram, index: number) => <PartDisplay key={part.key} index={index} part={part} />)}
      {lessons.length > 0 && (
        <View style={styles.lessonsBox}>
          {lessons.map((lesson: Lesson, index: number) => (
            <LessonDisplay key={lesson.key} index={index} isTopLevel={true} lesson={lesson} />
          ))}
        </View>
      )}
    </View>
  );
}

function PartDisplay({ part, index }: { part: PartProgram; index: number }) {
  const lessons = part.lessons || [];
  const showPartDetails = part.summary || lessons.length > 0;

  return (
    <View style={styles.courseBox}>
      <View style={styles.headerBox} wrap={false}>
        <Text style={styles.courseIndex}>{String(index + 1).padStart(2, '0')}.</Text>
        <Text style={styles.courseTitle}>{part.title}</Text>
        {part.displayRawDuration && (
          <View style={styles.courseDuration}>
            <Text style={styles.courseDurationText}>{part.displayRawDuration}</Text>
          </View>
        )}
      </View>
      {showPartDetails && (
        <View style={styles.lessonsBox}>
          {part.summary && <Text style={styles.courseSummary}>{part.summary}</Text>}
          {lessons.map((lesson: Lesson, i: number) => (
            <LessonDisplay key={i} index={i} isTopLevel={false} lesson={lesson} />
          ))}
        </View>
      )}
    </View>
  );
}

function LessonDisplay({ lesson, index, isTopLevel }: { lesson: Lesson; index: number; isTopLevel: boolean }) {
  const isProject = lesson.isProjectLesson;

  return (
    <View
      // Only display a border above the first lesson if the lesson is inside a part (separating between the part's summary and its lessons).
      wrap={false}
      style={{
        ...styles.lessonDisplayRow,
        borderTopWidth: isTopLevel && index === 0 ? 0 : 1,
      }}
    >
      <View
        style={{
          ...styles.lessonIndexBackgroundPill,
          backgroundColor: isProject ? '#02A083' : '#142580',
        }}
      >
        <Text style={styles.lessonIndex}> {`L${index + 1}`}</Text>
      </View>
      <View style={styles.lessonTitle}>
        {isProject && <Text style={{ color: '#02A083' }}>Project:</Text>}
        <Text>{lesson.title}</Text>
      </View>
      <Text style={styles.lessonSummary}>{lesson.summary}</Text>
    </View>
  );
}
